/** @module @liquidefy/code-editor/themes/identity/syntax-palette @version 1.0.0 @license LQD Research LLC */

/**
 * Liquidefy Syntax Color Philosophy — the opinionated syntax color mapping
 * that defines "what Liquidefy code looks like."
 *
 * Each syntax category has a canonical OKLCH hue assignment with a clear
 * rationale. Dark and light modes share the same hue assignments but
 * differ in lightness and chroma.
 *
 * Pure functions. No DOM, no side effects.
 *
 * @rust syntax_palette.rs — false; 4/2/26; sm
 */

// ═══ §1 IMPORTS ═══

import {
  hexToOKLCH,
  oklchToHex,
  luminance,
} from '../color/oklch.js';
import type { OKLCH } from '../color/oklch.js';
import type { TokenColor } from '../../code-editor-types.js';

// ═══ §2 TYPES ═══

/**
 * All recognized syntax categories.
 */
export type SyntaxCategory =
  | 'keyword'
  | 'string'
  | 'comment'
  | 'type'
  | 'function'
  | 'variable'
  | 'constant'
  | 'number'
  | 'operator'
  | 'punctuation'
  | 'property'
  | 'parameter'
  | 'decorator'
  | 'tag'
  | 'attribute'
  | 'regex'
  | 'escape'
  | 'error'
  | 'namespace'
  | 'label'
  | 'builtin';

/**
 * A complete syntax palette: a color for every recognized category,
 * plus the font style for each.
 */
export type SyntaxPalette = Readonly<Record<SyntaxCategory, {
  readonly color: string;
  readonly fontStyle?: string;
}>>;

// ═══ §3 HUE ASSIGNMENTS ═══

/**
 * The canonical OKLCH hue assignments (in degrees) for each syntax category.
 * This is the "soul" of the Liquidefy look.
 */
const SYNTAX_HUES: Readonly<Record<SyntaxCategory, number>> = {
  keyword:     260,  // blue-violet — structural, subtle
  string:       60,  // amber/orange — warm, inviting
  comment:     220,  // cool gray-blue — clearly secondary
  type:        185,  // teal/cyan — "what is this"
  function:    145,  // soft green — "what does this do"
  variable:      0,  // achromatic — most common, minimal color
  constant:     40,  // gold — special but not rare
  number:       50,  // yellow-gold — warm, distinct from strings
  operator:      0,  // achromatic — structural, fades into bg
  punctuation:   0,  // achromatic — structural, fades into bg
  property:    200,  // slate blue
  parameter:    30,  // warm neutral
  decorator:   310,  // magenta — decorative, vivid
  tag:          15,  // coral-red — for HTML/JSX
  attribute:   170,  // sea-green
  regex:       290,  // purple
  escape:      280,  // blue-purple
  error:        25,  // rose-red
  namespace:   210,  // steel blue
  label:       120,  // lime
  builtin:     190,  // cyan
};

/**
 * Font style assignments per category.
 */
const SYNTAX_STYLES: Partial<Record<SyntaxCategory, string>> = {
  comment: 'italic',
  keyword: '',
  decorator: 'italic',
  parameter: 'italic',
};

/**
 * Get the canonical hue assignments.
 * Returned as a pure data snapshot.
 */
export function getLiquidefySyntaxHues(): Record<SyntaxCategory, number> {
  return { ...SYNTAX_HUES };
}

// ═══ §4 PALETTE GENERATION ═══

/** Clamp to [min, max] */
function clamp(x: number, min: number, max: number): number {
  return x < min ? min : x > max ? max : x;
}

/**
 * Derive lightness and chroma for a category in a given mode.
 *
 * Design rules:
 * - Dark mode: foreground colors are lighter (L 0.65-0.88), moderate chroma
 * - Light mode: foreground colors are darker (L 0.30-0.55), moderate chroma
 * - Achromatic categories (variable, operator, punctuation): near zero chroma
 * - Comments: low chroma, reduced lightness contrast
 * - Decorators: higher chroma (they're rare, can be vivid)
 */
function categoryParams(cat: SyntaxCategory, mode: 'dark' | 'light'): { L: number; C: number } {
  const isAchromatic = cat === 'variable' || cat === 'operator' || cat === 'punctuation';
  const isComment = cat === 'comment';
  const isDecorator = cat === 'decorator';
  const isError = cat === 'error';

  if (mode === 'dark') {
    if (isAchromatic)  return { L: 0.82, C: 0.01 };
    if (isComment)     return { L: 0.52, C: 0.03 };
    if (isDecorator)   return { L: 0.76, C: 0.17 };
    if (isError)       return { L: 0.72, C: 0.20 };
    // Standard syntax token
    return { L: 0.76, C: 0.12 };
  } else {
    // Light mode
    if (isAchromatic)  return { L: 0.35, C: 0.01 };
    if (isComment)     return { L: 0.58, C: 0.03 };
    if (isDecorator)   return { L: 0.48, C: 0.17 };
    if (isError)       return { L: 0.48, C: 0.20 };
    return { L: 0.45, C: 0.12 };
  }
}

/**
 * Create THE Liquidefy syntax palette for dark or light mode.
 *
 * Both modes use the same hue assignments — only lightness and chroma differ.
 * This ensures a consistent coding experience across modes.
 */
export function createLiquidelySyntaxPalette(mode: 'dark' | 'light'): SyntaxPalette {
  const categories = Object.keys(SYNTAX_HUES) as SyntaxCategory[];
  const entries: Record<string, { color: string; fontStyle?: string }> = {};

  for (let i = 0; i < categories.length; i++) {
    const cat = categories[i]!;
    const hue = SYNTAX_HUES[cat];
    const { L, C } = categoryParams(cat, mode);

    const color = oklchToHex({ L, C: clamp(C, 0, 0.35), H: hue });
    const fontStyle = SYNTAX_STYLES[cat];

    entries[cat] = fontStyle !== undefined ? { color, fontStyle } : { color };
  }

  return entries as unknown as SyntaxPalette;
}

// ═══ §5 TEXTMATE SCOPE MAPPING ═══

/**
 * Comprehensive TextMate scope mappings for each syntax category.
 * Maps Liquidefy's semantic categories to the scopes that editors use.
 */
const SCOPE_MAP: Readonly<Record<SyntaxCategory, ReadonlyArray<string>>> = {
  keyword: [
    'keyword',
    'keyword.control',
    'keyword.operator.new',
    'keyword.operator.expression',
    'keyword.operator.logical',
    'storage.type',
    'storage.modifier',
  ],
  string: [
    'string',
    'string.quoted',
    'string.template',
    'string.interpolated',
  ],
  comment: [
    'comment',
    'comment.line',
    'comment.block',
    'comment.block.documentation',
  ],
  type: [
    'entity.name.type',
    'entity.name.class',
    'entity.name.interface',
    'entity.name.enum',
    'support.type',
    'support.class',
    'entity.other.inherited-class',
  ],
  function: [
    'entity.name.function',
    'support.function',
    'meta.function-call',
    'entity.name.method',
  ],
  variable: [
    'variable',
    'variable.other',
    'variable.other.readwrite',
    'variable.language',
  ],
  constant: [
    'constant',
    'constant.language',
    'constant.other',
    'variable.other.constant',
  ],
  number: [
    'constant.numeric',
    'constant.numeric.integer',
    'constant.numeric.float',
    'constant.numeric.hex',
  ],
  operator: [
    'keyword.operator',
    'keyword.operator.assignment',
    'keyword.operator.arithmetic',
    'keyword.operator.comparison',
  ],
  punctuation: [
    'punctuation',
    'punctuation.definition',
    'punctuation.separator',
    'punctuation.terminator',
    'meta.brace',
  ],
  property: [
    'variable.other.property',
    'support.type.property-name',
    'entity.name.tag.css',
    'meta.object-literal.key',
  ],
  parameter: [
    'variable.parameter',
    'variable.parameter.function',
  ],
  decorator: [
    'entity.name.decorator',
    'meta.decorator',
    'punctuation.decorator',
    'entity.name.function.decorator',
  ],
  tag: [
    'entity.name.tag',
    'entity.name.tag.html',
    'entity.name.tag.xml',
    'support.class.component',
  ],
  attribute: [
    'entity.other.attribute-name',
    'entity.other.attribute-name.html',
  ],
  regex: [
    'string.regexp',
    'constant.other.character-class.regexp',
    'keyword.operator.quantifier.regexp',
  ],
  escape: [
    'constant.character.escape',
    'constant.character.entity',
  ],
  error: [
    'invalid',
    'invalid.illegal',
    'invalid.deprecated',
  ],
  namespace: [
    'entity.name.namespace',
    'entity.name.module',
    'entity.name.package',
  ],
  label: [
    'entity.name.label',
    'entity.name.goto',
  ],
  builtin: [
    'support.function.builtin',
    'support.variable.builtin',
    'support.constant.builtin',
    'entity.name.type.primitive',
  ],
};

/**
 * Convert a SyntaxPalette to a ThemeDefinition-compatible TokenColor array
 * with comprehensive TextMate scope mappings.
 */
export function syntaxPaletteToTokenColors(palette: SyntaxPalette): TokenColor[] {
  const categories = Object.keys(SCOPE_MAP) as SyntaxCategory[];
  const result: TokenColor[] = [];

  for (let i = 0; i < categories.length; i++) {
    const cat = categories[i]!;
    const entry = palette[cat];
    const scopes = SCOPE_MAP[cat];

    // Create one TokenColor entry per scope group for the category
    result.push({
      scope: scopes as unknown as string[],
      settings: {
        foreground: entry.color,
        ...(entry.fontStyle !== undefined ? { fontStyle: entry.fontStyle } : {}),
      },
    });
  }

  return result;
}

// ═══ §6 BACKGROUND CONTRAST ADJUSTMENT ═══

/**
 * Adjust a palette so every syntax color meets a minimum contrast
 * against a specific background color.
 *
 * Uses OKLCH lightness to push colors that are too close to the
 * background further away in perceptual lightness.
 *
 * @param palette - Source palette
 * @param bgHex  - Background color to contrast against
 * @returns Adjusted palette with guaranteed readability
 */
export function adjustPaletteForBackground(
  palette: SyntaxPalette,
  bgHex: string,
): SyntaxPalette {
  const bgOK = hexToOKLCH(bgHex);
  const isDark = bgOK.L < 0.5;
  const minDeltaL = 0.35; // Minimum lightness difference for readability

  const categories = Object.keys(palette) as SyntaxCategory[];
  const adjusted: Record<string, { color: string; fontStyle?: string }> = {};

  for (let i = 0; i < categories.length; i++) {
    const cat = categories[i]!;
    const entry = palette[cat];
    const oklch = hexToOKLCH(entry.color);

    const deltaL = Math.abs(oklch.L - bgOK.L);
    if (deltaL < minDeltaL) {
      // Push lightness away from background
      let newL: number;
      if (isDark) {
        newL = bgOK.L + minDeltaL + (minDeltaL - deltaL) * 0.5;
      } else {
        newL = bgOK.L - minDeltaL - (minDeltaL - deltaL) * 0.5;
      }
      newL = clamp(newL, 0, 1);
      adjusted[cat] = {
        color: oklchToHex({ L: newL, C: oklch.C, H: oklch.H }),
        fontStyle: entry.fontStyle,
      };
    } else {
      adjusted[cat] = { ...entry };
    }
  }

  return adjusted as unknown as SyntaxPalette;
}

// ═══ §7 SELF-DOCUMENTATION ═══

/**
 * Returns each syntax category with its hue and design rationale.
 * Self-documenting function: explains WHY each color was chosen.
 */
export function getLiquidelySyntaxSummary(): Record<string, { hue: number; rationale: string }> {
  return {
    keyword:     { hue: 260, rationale: 'Blue-violet. Structures code without shouting. Cool and reserved.' },
    string:      { hue: 60,  rationale: 'Amber/orange. Warm and inviting. Distinct from structural tokens.' },
    comment:     { hue: 220, rationale: 'Cool gray-blue. Clearly secondary. Italic for visual separation.' },
    type:        { hue: 185, rationale: 'Teal/cyan. "What is this?" Cool but distinct from keywords.' },
    function:    { hue: 145, rationale: 'Soft green. "What does this do?" Clean and recognizable.' },
    variable:    { hue: 0,   rationale: 'Near-foreground achromatic. Most common token, minimal distraction.' },
    constant:    { hue: 40,  rationale: 'Gold. Slightly bolder than variables. Special but not rare.' },
    number:      { hue: 50,  rationale: 'Yellow-gold. Warm but distinct from strings.' },
    operator:    { hue: 0,   rationale: 'Achromatic. Structural punctuation that fades into background.' },
    punctuation: { hue: 0,   rationale: 'Achromatic. Structural, should fade. Not worth color real estate.' },
    property:    { hue: 200, rationale: 'Slate blue. Object properties need their own identity.' },
    parameter:   { hue: 30,  rationale: 'Warm neutral. Italic. Distinct from regular variables.' },
    decorator:   { hue: 310, rationale: 'Magenta. Decorative, special, rare. Can afford to be vivid.' },
    tag:         { hue: 15,  rationale: 'Coral-red. HTML/JSX tags need strong visual anchoring.' },
    attribute:   { hue: 170, rationale: 'Sea-green. Complements tag color without competing.' },
    regex:       { hue: 290, rationale: 'Purple. Regex is unusual syntax, deserves unusual color.' },
    escape:      { hue: 280, rationale: 'Blue-purple. Close to regex but distinct. Escape sequences are special.' },
    error:       { hue: 25,  rationale: 'Rose-red. Clear signal without being jarring.' },
    namespace:   { hue: 210, rationale: 'Steel blue. Organizational, calm, professional.' },
    label:       { hue: 120, rationale: 'Lime. Rare in modern code, vivid when it appears.' },
    builtin:     { hue: 190, rationale: 'Cyan. Built-in functions/types are special but common.' },
  };
}
